import { useEffect, useState } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { CgEye, CgTrash } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import CustomModal from "../Commaon/CustomModal";

const UsersList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    const getUsers = async () => {
      setLoading(true);
      try {
        const response = await AuthInterceptor(
          `/admin/user?page=${pageNo}&limit=${limit}`
        );
        const data = response.data;

        if (data.s === 200) {
          setUsers(data.r || []);
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to fetch users:", err);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    getUsers();
  }, [pageNo, limit]);

  const toggleUserStatus = async (id, newStatus) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/user/${id}`, {
        method: "PUT",
        body: JSON.stringify({ isActive: newStatus }),
      });
      const data = response.data;

      if (data.s === 200) {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user._id === id ? { ...user, isActive: newStatus } : user
          )
        );
        setSnack({
          notification: { message: "User status updated", type: "success" },
        });
      }
    } catch (err) {
      setSnack({
        notification: { message: "Error updating user status", type: "error" },
      });
      console.error("Error updating user status:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = Array.isArray(users)
    ? users.filter(
        (u) =>
          u.fullName?.toLowerCase().includes(query.toLowerCase()) ||
          u.email?.toLowerCase().includes(query.toLowerCase())
      )
    : [];

  const viewUser = (user) => {
    navigate(`/user/${user._id}`);
  };

  const deleteUser = async (id) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/user/${id}`, {
        method: "DELETE",
      });
      const data = response.data;

      if (data.s === 200) {
        setUsers((prevUsers) => prevUsers.filter((user) => user._id !== id));
        setSnack({
          notification: { message: "User deleted successfully", type: "success" },
        });
      } else {
        throw new Error(data.m || "Error deleting user");
      }
    } catch (err) {
      setSnack({
        notification: { message: err.message || "Error deleting user", type: "error" },
      });
    } finally {
      setLoading(false);
      setDeleteModel(false);
      setDeleteId(null);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        {/* Header + Page Size */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h2 className="mb-2 mb-md-0">Users</h2>
          <div>
            <select
              className="form-select form-select-sm w-auto"
              value={limit}
              onChange={(e) =>
                setPageNo(1) || setUsers([]) || setLimit(Number(e.target.value))
              }
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search */}
        <div className="input-group mb-3">
          <input
            className="form-control"
            placeholder="Search users..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn btn-outline-secondary">Search</button>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th>Sr.</th>
                <th>Name</th>
                <th>Email</th>
                <th >Mobile</th>
                <th >Role</th>
                <th>Orders</th>
                <th>Status</th>
                <th style={{ width: "150px" }} className="text-start">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                filteredUsers.length > 0 &&
                filteredUsers.map((u, idx) => (
                  <tr key={u._id || idx}>
                    <td>{idx + 1}</td>
                    <td>{u.fullName || "-"}</td>
                    <td>{u.email || "-"}</td>
                    <td >
                      {u.mobileNumber || "-"}
                    </td>
                    <td >
                      <span className="badge text-primary">
                        {u.role || "-"}
                      </span>
                    </td>
                    <td>{u.totalOrders ?? 0}</td>
                    <td>
                      {u.isActive ? (
                        <span
                          className="badge bg-success"
                          role="button"
                          onClick={() => toggleUserStatus(u._id, false)}
                        >
                          Active
                        </span>
                      ) : (
                        <span
                          className="badge bg-secondary"
                          role="button"
                          onClick={() => toggleUserStatus(u._id, true)}
                        >
                          Inactive
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => viewUser(u)}
                      >
                        <CgEye color="#666cff" />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger ms-2"
                        onClick={() => {
                          setDeleteId(u._id);
                          setDeleteModel(true);
                        }}
                      >
                        <CgTrash />
                      </button>
                    </td>
                  </tr>
                ))}

              {!loading && filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex flex-wrap justify-content-between align-items-center m-3 gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
            disabled={pageNo === 1}
          >
            Prev
          </button>
          <span className="mx-2">Page {pageNo}</span>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setPageNo((prev) => prev + 1)}
          >
            Next
          </button>
        </div>
      </div>
      <CustomModal
        show={deleteModel}
        title="Confirm Delete"
        message="Are you sure you want to permanently delete this user?"
        onConfirm={() => deleteUser(deleteId)}
        onCancel={() => {
          setDeleteModel(false);
          setDeleteId(null);
        }}
      />
    </>
  );
};

export default UsersList;
